export type SplitLine = { petId: string | null; lineTotal: number };
export type PetLineGroup = { petId: string | null; lineTotal: number; count: number };
export type PetAmount = { petId: string | null; amount: number };

/** Linhas agrupadas por pet, na ordem em que aparecem; linha sem pet usa o pet da venda. */
export function groupLinesByPet(lines: SplitLine[], salePetId: string | null): PetLineGroup[] {
  const groups = new Map<string | null, PetLineGroup>();
  for (const line of lines) {
    const petId = line.petId ?? salePetId ?? null;
    const group = groups.get(petId) ?? { petId, lineTotal: 0, count: 0 };
    group.lineTotal += line.lineTotal;
    group.count += 1;
    groups.set(petId, group);
  }
  return [...groups.values()];
}

/**
 * Divide um valor (um pagamento ou o total da venda) entre os pets na proporção das linhas de cada um.
 * Trabalha em centavos e distribui o resto pelo maior resto, então a soma das partes é sempre o valor inteiro.
 * Sem valor nas linhas, tudo vai para o pet da venda.
 */
export function splitAmountByPet(amount: number, lines: SplitLine[], salePetId: string | null): PetAmount[] {
  const totalCents = Math.round(amount * 100);
  const groups = groupLinesByPet(lines, salePetId);
  const weight = groups.reduce((sum, group) => sum + Math.max(0, group.lineTotal), 0);

  if (groups.length === 0 || weight <= 0) {
    return [{ petId: salePetId ?? null, amount: totalCents / 100 }];
  }

  const exact = groups.map((group) => (totalCents * Math.max(0, group.lineTotal)) / weight);
  const cents = exact.map((value) => Math.floor(value));
  let remainder = totalCents - cents.reduce((sum, value) => sum + value, 0);
  const byLargestFraction = exact
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort((a, b) => b.fraction - a.fraction || a.index - b.index);
  for (const { index } of byLargestFraction) {
    if (remainder <= 0) break;
    cents[index] += 1;
    remainder -= 1;
  }

  return groups.map((group, index) => ({ petId: group.petId, amount: cents[index] / 100 }));
}

/**
 * Nome do pet de cada item, para o detalhe da venda. Só rotula quando a venda é de mais de um pet
 * (com um pet só o rótulo seria ruído); item sem pet usa o pet da venda; pet sem nome conhecido não é rotulado.
 */
export function labelItemPets(
  items: Array<{ id: string; petId: string | null }>,
  salePetId: string | null,
  petNames: Map<string, string>,
): Map<string, string> {
  const labels = new Map<string, string>();
  const resolved = items.map((item) => ({ id: item.id, petId: item.petId ?? salePetId ?? null }));
  if (new Set(resolved.map((item) => item.petId).filter(Boolean)).size < 2) return labels;
  for (const item of resolved) {
    const name = item.petId ? petNames.get(item.petId) : undefined;
    if (name) labels.set(item.id, name);
  }
  return labels;
}
